import moment from 'moment';
import { OrderStatus, PaymentMethod, HolidayOrder, Airport } from './types';
import { getCommonAirportDataApi } from 'app/services/holidayApi';

// Cache for airports data
let airportsCache: Airport[] | null = null;

// Fetch airports data (with caching)
export const fetchAirportsData = async (): Promise<Airport[]> => {
  const cachedData = airportsCache;
  if (cachedData !== null) {
    return cachedData;
  }

  try {
    const response = await getCommonAirportDataApi({});
    const data = response.data || response;

    if (!Array.isArray(data)) {
      console.warn('Unexpected airport payload, expected array:', data);
      airportsCache = [];
      return [];
    }

    const airports: Airport[] = data.map((item: any) => ({
      code: item.code,
      name: item.name || item.nameVN,
      cityName: item.cityName || item.city,
      terminal: item.terminal,
    }));
    airportsCache = airports;
    return airports;
  } catch (error) {
    console.error('Failed to fetch airports:', error);
    return [];
  }
};

// Get airport display name from code
export const getAirportDisplay = (
  code: string,
  airports: Airport[],
): string => {
  const airport = airports.find(a => a.code === code);
  if (airport) {
    return `${airport.cityName || airport.name} (${code})`;
  }
  return code;
};

// Get full airport info
export const getFullAirportInfo = (
  code: string,
  airports: Airport[],
): string => {
  const airport = airports.find(a => a.code === code);
  if (airport) {
    const terminal = airport.terminal ? ` ${airport.terminal}` : '';
    return `${code} ${airport.name}${terminal}`;
  }
  return code;
};

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  [OrderStatus.ALL]: 'Tất cả',
  [OrderStatus.PENDING_CONFIRMATION]: 'Chờ duyệt',
  [OrderStatus.CONFIRMED]: 'Duyệt thành công',
  [OrderStatus.UNCONFIRMED]: 'Từ chối',
  [OrderStatus.CANCELLED]: 'Đã hủy',
};

export const ORDER_STATUS_COLORS: Record<OrderStatus, string> = {
  [OrderStatus.PENDING_CONFIRMATION]: '#faad14', // orange
  [OrderStatus.CONFIRMED]: '#52c41a', // green
  [OrderStatus.UNCONFIRMED]: '#ff4d4f', // red
  [OrderStatus.CANCELLED]: '#8c8c8c', // gray
  [OrderStatus.ALL]: '#8c8c8c', // gray
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  [PaymentMethod.DEPOSIT]: 'Deposit',
  [PaymentMethod.CREDIT]: 'Credit',
  [PaymentMethod.CKTM]: 'CKTM',
};

export const STATUS_OPTIONS = [
  { label: ORDER_STATUS_LABELS[OrderStatus.ALL], value: OrderStatus.ALL },
  {
    label: ORDER_STATUS_LABELS[OrderStatus.PENDING_CONFIRMATION],
    value: OrderStatus.PENDING_CONFIRMATION,
  },
  {
    label: ORDER_STATUS_LABELS[OrderStatus.CONFIRMED],
    value: OrderStatus.CONFIRMED,
  },
  {
    label: ORDER_STATUS_LABELS[OrderStatus.UNCONFIRMED],
    value: OrderStatus.UNCONFIRMED,
  },
  {
    label: ORDER_STATUS_LABELS[OrderStatus.CANCELLED],
    value: OrderStatus.CANCELLED,
  },
];

export const PAYMENT_METHOD_OPTIONS = [
  { label: 'Tất cả', value: '' },
  {
    label: PAYMENT_METHOD_LABELS[PaymentMethod.DEPOSIT],
    value: PaymentMethod.DEPOSIT,
  },
  {
    label: PAYMENT_METHOD_LABELS[PaymentMethod.CREDIT],
    value: PaymentMethod.CREDIT,
  },
  {
    label: PAYMENT_METHOD_LABELS[PaymentMethod.CKTM],
    value: PaymentMethod.CKTM,
  },
];

/**
 * Format currency to Vietnamese Dong
 * @param amount - amount in VND
 * @returns formatted string like "100.000đ"
 */
export const formatCurrency = (amount: number): string => {
  return `${amount.toLocaleString('vi-VN')}đ`;
};

/**
 * Calculate remaining time from expiredAt
 * @param expiredAt - ISO datetime string or "HH:mm:ss DD/MM/YYYY" format
 * @returns formatted countdown string like "02:30:15" or "Hết hạn" if expired
 */
export const calculateCountdown = (expiredAt: string): string => {
  if (!expiredAt) return 'Hết hạn';

  const now = moment();

  // Try parsing with the API format "HH:mm:ss DD/MM/YYYY"
  let expired = moment(expiredAt, 'HH:mm:ss DD/MM/YYYY', true);

  // Fallback to default parsing for ISO format
  if (!expired.isValid()) {
    expired = moment(expiredAt);
  }

  const diff = expired.diff(now);

  if (diff <= 0) {
    return 'Hết hạn';
  }

  const duration = moment.duration(diff);
  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();
  const seconds = duration.seconds();

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0',
  )}:${String(seconds).padStart(2, '0')}`;
};

/**
 * Get total quantity from order items
 * @param order - HolidayOrder object
 * @returns total quantity
 */
export const getTotalQuantity = (order: HolidayOrder): number => {
  // prefer summing ticket quantities if available, otherwise fall back to combo quantity
  let total = 0;
  order.orderItems.forEach(item => {
    if (item.tickets && item.tickets.length > 0) {
      total += item.tickets.reduce((s, t) => s + (t.quantity || 0), 0);
    } else if (item.combo && typeof item.combo.quantity === 'number') {
      total += item.combo.quantity;
    }
  });
  return total;
};

/**
 * Format datetime to dd/mm/yyyy hh:mm:ss
 * @param datetime - Date string in format "HH:mm:ss DD/MM/YYYY" or ISO string
 * @returns formatted string
 */
export const formatDateTime = (datetime: string): string => {
  if (!datetime) return '';

  // Try parsing with the API format "HH:mm:ss DD/MM/YYYY"
  const parsed = moment(datetime, 'HH:mm:ss DD/MM/YYYY', true);
  if (parsed.isValid()) {
    return parsed.format('DD/MM/YYYY HH:mm:ss');
  }

  // Fallback to default parsing for ISO format
  return moment(datetime).format('DD/MM/YYYY HH:mm:ss');
};
